<script setup>
import { computed, watch } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import ChatwootWidget from '@/Components/Public/ChatwootWidget.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import ThemeMenu from '@/Components/Public/ThemeMenu.vue'
import BrandHeader from '@/Components/Public/BrandHeader.vue'
import BrandFooter from '@/Components/Public/BrandFooter.vue'

const page = usePage()
const { t, locale } = useI18n()

const brand    = computed(() => page.props.brand || {})
const current  = computed(() => page.props.locale || 'en')
const flash    = computed(() => page.props.flash || {})
const partnersBase = computed(() => page.props.partners_base || 'https://partners.getphuket.com')

const footerChannels = computed(() => {
  const c = brand.value.channels || {}
  return [
    ...(c.message     || []),
    ...(c.social_page || []),
    ...(c.support     || []),
    ...(c.website     || [])
  ]
})

function platformLabel(p) {
  return p.charAt(0).toUpperCase() + p.slice(1)
}

watch(current, (val) => { locale.value = val }, { immediate: true })

// Prepend the active locale prefix to an absolute app path (en = no prefix).
function localePath(path) {
  const loc = current.value
  if (!loc || loc === 'en') return path
  return `/${loc}${path === '/' ? '' : path}`
}

function setLang(lang) {
  if (lang === current.value) return
  const bare = window.location.pathname.replace(/^\/(ru|zh|th)(?=\/|$)/, '') || '/'
  const path = lang === 'en' ? bare : `/${lang}${bare === '/' ? '' : bare}`
  window.location.assign(`${path}?lang=${lang}`)
}

// Display-currency picker. Server honours ?currency= and persists in a
// cookie, so the next page load keeps the choice without us managing state.
const displayCurrency   = computed(() => page.props.display_currency || 'THB')
const displayCurrencies = computed(() => page.props.display_currencies || ['THB'])
const hasCurrencyPicker = computed(() => displayCurrencies.value.length > 1)

function setCurrency(code) {
  if (code === displayCurrency.value) return
  router.get(window.location.pathname, { currency: code }, { preserveScroll: true, preserveState: false })
}

const localeOptions = [
  { value: 'en', label: 'English', sub: 'EN' },
  { value: 'ru', label: 'Русский', sub: 'RU' },
  { value: 'zh', label: '中文',    sub: 'ZH' },
  { value: 'th', label: 'ไทย',     sub: 'TH' }
]
</script>

<template>
  <div class="public-shell">
    <BrandHeader :home-href="localePath('/')" :brand-label="brand.name || 'Get Phuket'">
      <template #nav>
        <nav class="public-nav">
          <a :href="localePath('/tours')" class="public-nav__link">{{ t('public.nav.tours') }}</a>
          <a :href="localePath('/rentals')" class="public-nav__link">{{ t('public.nav.rentals') }}</a>
          <a :href="localePath('/property')" class="public-nav__link">{{ t('public.nav.property') }}</a>
          <a :href="partnersBase" class="public-nav__link">{{ t('public.nav.partners') }}</a>
        </nav>
      </template>
      <template #tools>
        <div class="public-header__tools">
          <!-- Currency -->
          <HeaderMenu v-if="hasCurrencyPicker" :aria-label="t('public.menu.currency')">
            <template #icon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M14 9a2.5 2.5 0 0 0-2.5-2.5h-1A2.5 2.5 0 0 0 8 9c0 1.5 1 2.2 3 2.5s3 1 3 2.5a2.5 2.5 0 0 1-2.5 2.5h-1A2.5 2.5 0 0 1 8 14"/>
                <path d="M11 5v1.5M11 16.5V18"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button v-for="code in displayCurrencies" :key="code" type="button" class="hmenu__item" @click="setCurrency(code); close()">
                <span>{{ code }}</span>
                <svg v-if="code === displayCurrency" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Language -->
          <HeaderMenu :aria-label="t('public.menu.language')">
            <template #icon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M3 12h18"/>
                <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="setLang(opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === current" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Theme -->
          <ThemeMenu />
        </div>
      </template>
    </BrandHeader>

    <div v-if="flash.alert" role="alert" class="pub-flash pub-flash--alert">
      {{ flash.alert }}
    </div>
    <div v-if="flash.notice" role="status" class="pub-flash pub-flash--notice">
      {{ flash.notice }}
    </div>

    <main class="public-main">
      <slot />
    </main>

    <BrandFooter>
      <span>© {{ new Date().getFullYear() }} {{ brand.name || 'Get Phuket' }}</span>
      <template v-for="c in footerChannels" :key="c.id">
        <span class="public-footer__dot">·</span>
        <a :href="c.url" target="_blank" rel="noopener" class="public-footer__link">{{ c.label || platformLabel(c.platform) }}</a>
      </template>
    </BrandFooter>
    <ChatwootWidget />
  </div>
</template>

<style scoped>
.public-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, color-mix(in oklch, var(--accent) 12%, transparent) 0%, transparent 60%),
    var(--bg);
  color: var(--ink);
}

.public-nav {
  display: none;
  gap: 1.25rem;
  margin-right: auto;
  margin-left: 2rem;
  font-size: 0.9375rem;
}
@media (min-width: 640px) { .public-nav { display: inline-flex; } }
.public-nav__link {
  color: var(--inkSoft);
  text-decoration: none;
  font-weight: 500;
}
.public-nav__link:hover { color: var(--ink); }

.public-header__tools {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  padding-left: 0.5rem;
  border-left: 1px solid color-mix(in oklch, var(--line) 80%, transparent);
}

/* Slot-projected styles target descendants of HeaderMenu's panel */
.public-header__tools :deep(.hmenu__item) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: var(--ink);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.public-header__tools :deep(.hmenu__item:hover) {
  background: color-mix(in oklch, var(--ink) 6%, transparent);
}
.public-header__tools :deep(.hmenu__item-sub) {
  color: var(--muted);
  font-size: 0.8125rem;
  margin-left: 0.125rem;
}
.public-header__tools :deep(.hmenu__check) {
  width: 1rem;
  height: 1rem;
  color: var(--accent);
  flex-shrink: 0;
}

.public-main {
  flex: 1;
  display: block;
}

.public-footer__link {
  color: var(--muted);
  text-decoration: none;
}
.public-footer__link:hover { color: var(--ink); }
.public-footer__dot { opacity: 0.5; }

.pub-flash {
  padding: 0.75rem 1rem;
  margin: 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}
.pub-flash--alert {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
  border: 1px solid color-mix(in oklch, var(--danger) 30%, transparent);
}
.pub-flash--notice {
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
  color: var(--ok);
  border: 1px solid color-mix(in oklch, var(--ok) 30%, transparent);
}
</style>
